import { colors, ScreenLoading, SplitView, Stack, Typography, useSplitView } from '@ui-components/neo.tax';
import { useCallback, useEffect, useState } from 'react';

import {
  calculateAverage,
  FilterPopover,
  KeywordSearch,
  SortNulls,
  SortPopover,
  useFilterConditions,
  useKeywordSearch,
  useSorting,
  SredProjectSortFields,
  SortDirection,
  ProjectsTable,
} from '@neo.tax/shared';
import { filterableSredProjectColumns } from '@neo.tax/sred/constants';
import { useSredProjects, type SredProject } from '@neo.tax/sred/hooks';
import { SredLockPageLayout } from '@neo.tax/sred/organisms/sred-lock-page-layout';
import { mapStringToSortField } from '@neo.tax/sred/utils';

import { useSredProjectsTableColumns } from './hooks/useSredProjectsTableColumns';

const PROJECT_DRAWER_WIDTH = 412;

export const SredProjectsLayout = (): React.ReactElement => {
  const sredProjectId = undefined; // TODO: Implement selection from URL param + project drawer

  const { fixedWidth, isOpen, setIsOpen } = useSplitView({
    initialOpen: !!sredProjectId,
    fixedWidth: PROJECT_DRAWER_WIDTH,
  });

  const [propSredProjectId, setPropSredProjectId] = useState(sredProjectId);

  const searchKeywordState = useKeywordSearch();
  const filterState = useFilterConditions({ columns: filterableSredProjectColumns, persistenceKey: 'sredProjects' });
  const sortState = useSorting({ columns: filterableSredProjectColumns, persistenceKey: 'sredProjects' });

  const {
    isLoadingMore,
    loading: projectsLoading,
    onLoadMoreItems,
    sredProjects,
  } = useSredProjects({
    searchKeyword: searchKeywordState.searchKeyword,
    filterConditions: filterState.conditions,
    sorting: [
      ...sortState.sorts.map((s) => ({
        field: mapStringToSortField(s.column),
        direction: s.direction,
        nulls: SortNulls.NullsLast,
      })),
      // Add ID as secondary sort to ensure deterministic ordering for cursor pagination
      {
        field: SredProjectSortFields.Id,
        direction: SortDirection.Asc,
        nulls: SortNulls.NullsLast,
      },
    ],
  });

  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const handleSelectTableRow = (checked: boolean, sredProject: SredProject): void => {
    setSelectedRows((prev) => (checked ? [...prev, sredProject.id] : prev.filter((id) => id !== sredProject.id)));
  };

  const handleSelectAllTableRows = (checked: boolean): void => {
    if (!sredProjects) {
      setSelectedRows([]);
      return;
    }
    setSelectedRows(checked ? sredProjects.map((sredProject) => sredProject.id) : []);
  };

  // Detects changes in projectId
  useEffect(() => {
    if (sredProjectId) {
      setPropSredProjectId(sredProjectId);
    }
    setIsOpen(!!sredProjectId);
  }, [sredProjectId, setIsOpen]);

  const handleTransitionEnd = useCallback(() => {
    setPropSredProjectId(sredProjectId);
  }, [sredProjectId]);

  const columns = useSredProjectsTableColumns({
    clickedRowId: propSredProjectId ?? undefined,
    onSelectAllRows: handleSelectAllTableRows,
    onSelectRow: handleSelectTableRow,
    rowCount: sredProjects ? sredProjects.length : 0,
    avgTicketCount: sredProjects ? calculateAverage(sredProjects, (sredProject) => sredProject.ticketCount) : 0,
    selectedRowIds: selectedRows,
  });

  if (projectsLoading && !sredProjects) {
    return <ScreenLoading />;
  }

  return (
    <SplitView
      mainView={
        <SredLockPageLayout
          title="SR&ED Projects"
          subtitle={
            <Typography fontSize="sm" color={colors.gray[500]}>
              Start by clicking on a project and qualifying its eligibility for the SR&ED credit with Neo.Tax&apos;s
              guidance!
            </Typography>
          }
          disableIfOngoingSync={true}
          fullWidth
        >
          <Stack direction="column" gap={6} fullHeight>
            <Stack gap={5}>
              <KeywordSearch {...searchKeywordState} />
              <FilterPopover itemsDescription="projects" {...filterState} />
              <SortPopover itemsDescription="projects" {...sortState} />
            </Stack>
            <ProjectsTable<SredProject>
              data={sredProjects || []}
              selectedProjectId={propSredProjectId ?? undefined}
              selectedRowIds={selectedRows}
              columns={columns}
              loadMoreItems={onLoadMoreItems}
              loadingRows={projectsLoading}
              loadingFooter={isLoadingMore}
            />
          </Stack>
        </SredLockPageLayout>
      }
      drawerView={propSredProjectId ? <div>SR&ED Project Detail - TODO</div> : null}
      isOpen={isOpen}
      fixedWidth={fixedWidth}
      onTransitionEnd={handleTransitionEnd}
    />
  );
};
